/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoggedInUser } from '@irian-ro/auth-shared-module';
import { OidcService } from '@irian-ro/auth-oidc';
import { StompService } from '@irian-ro/stomp-module';
import { SelectItem } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { animate, transition, trigger } from '@angular/animations';
import { EnvironmentService } from '@irian-ro/util-environment';
import { MainMenuItemsService } from './main-menu-items.service';
import { SelectItemsResourceService, SelectItemsStompService } from '@well/select-items-shell-api';
import { environment } from '../environments/environment';
import localeRo from '@angular/common/locales/ro';
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
import { registerLocaleData } from '@angular/common';
import { LoggedInContextService, WellContextService } from '@well/shared-providers';
import { ContextService } from './context.service';
import { WorkflowsStoreApiService } from '@well/iriro-frontend-libs/workflows/api';
import { translationSchema } from '@well/translation';
import { SelectItemsStoreFacade } from '@well/select-items-state';
import { TermsAndConditionsLocalStorageService } from '@well/shared-components';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { loginUrl } from '@well/navigation';

@Component({
  selector: 'well-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('move', [
      transition('initial=>final', animate('0.3s ease-in-out')),
      transition('final=>initial', animate('0.3s ease-in-out'))
    ])
  ],
  providers: [SelectItemsStompService],
})
export class AppComponent implements OnInit {
  context = inject(ContextService);
  private readonly destroyRef = inject(DestroyRef);

  userContextService = inject(LoggedInContextService);
  oidcService = inject(OidcService);
  workflows = inject(WorkflowsStoreApiService);

  title = 'well';

  env = environment;
  items$ = this.mainMenuItemsService.getMenu();
  languages: SelectItem[] | undefined = undefined;
  isWebSocketConnectionInitialized = false;
  defaultLang = 'ro';
  selectedLang: string | undefined = undefined;
  appContext = { reporter: 'notLoggedInUser', urlHistory: ['http://asd.ro', 'http://asd12.ro'] };

  currentState = 'initial';
  currentToken: string | undefined = undefined;

  isPaymentTermOverdue: boolean | null = null;
  loggedInUser: LoggedInUser | null = null;
  accessUnavailableFeatures = false;

  shouldSetLogoRouterLink = true;

  constructor(
    private translateService: TranslateService,
    private router: Router,
    private environmentService: EnvironmentService,
    private companyStompService: StompService,
    private mainMenuItemsService: MainMenuItemsService,
    private selectItemsResourceService: SelectItemsResourceService,
    private selectItemsStompService: SelectItemsStompService,
    private wellContextService: WellContextService,
    private selectItemsStoreFacade: SelectItemsStoreFacade,
    private termsAndConditionsLocalStorageService: TermsAndConditionsLocalStorageService
  ) {}

  ngOnInit(): void {
    this.accessUnavailableFeatures = this.environmentService.getEnvironmentProperty('accessUnavailableFeatures');
    this.registerLocals();
    this.oidcService.tryRestoreSession();
    this.subscribeToTokenExpiry();

    this.checkRouterLink(this.router.url);  // Initial check
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.checkRouterLink(event.urlAfterRedirects);
      }
    });
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationStart && !this.router.navigated) {
        this.termsAndConditionsLocalStorageService.clear();
      }
    });

    this.context.onLoggedIn()?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (context: any) => {
        const loggedInUser = context?.loggedInUser;
        this.loggedInUser = context?.loggedInUser;

        this.setContextForIssueReporter(loggedInUser);
        this.changeLanguage({ value: loggedInUser?.language });
        this.workflows.fetchAllConfigurations();

        if (!this.isWebSocketConnectionInitialized) {
          this.companyStompService.initializeWebSocketConnection(
            this.environmentService.getEnvironmentProperty('wsEndpointUrl')
          );
          this.isWebSocketConnectionInitialized = true;
        }

        this.loadSelectItems(loggedInUser?.language ? loggedInUser.language : this.defaultLang);
        this.initializeAndSubscribeToSelectItemsUpdates(
          loggedInUser?.language ? loggedInUser.language : this.defaultLang
        );

        this.wellContextService.getLoggedInContext().subscribe((loggedInContext: any) => {
          this.isPaymentTermOverdue = loggedInContext.paymentTermOverdue;
        });
      },
    });

    this.context.onLoggedOut()?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.companyStompService.disconnect();
        this.workflows.clearAllConfigurations();
        this.selectItemsStompService.stompTopicsUnsubscribe();
        this.isWebSocketConnectionInitialized = false;
        this.changeLanguage({ value: this.defaultLang });
        this.isPaymentTermOverdue = null;
        this.loggedInUser = null;
      },
    });

    this.languages = [
      { label: 'English', value: 'en' },
      { label: 'Romana', value: 'ro' }
    ];
  }

  private checkRouterLink(url: string): void {
    this.shouldSetLogoRouterLink = !url.includes('signup/step');
  }

  changeLanguage({
    value
  }: any) {
    this.selectedLang = value || this.defaultLang;
    this.translateService.use(this.selectedLang);
  }

  onActivate() {
    this.currentState = 'final';
  }

  onDeactivate() {
    this.currentState = 'initial';
  }

  private loadSelectItems(locale: string) {
    this.selectItemsResourceService.getSelectItemsByField().subscribe((res: any) => {
      this.selectItemsStoreFacade.setSelectItems(locale, res.data);
    });
  }

  private initializeAndSubscribeToSelectItemsUpdates(locale: string) {
    this.selectItemsStompService.initializeConnectionAndSubscribeTopics(locale);
    this.selectItemsStompService
      .getSelectItemsUpdateObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: any) => {
        this.selectItemsStoreFacade.setSelectItems(locale, res);
      });
  }

  private subscribeToTokenExpiry() {
    this.currentToken = this.oidcService.accessToken || undefined;
    this.oidcService.onTokenExpired
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.router.url.includes(loginUrl)) {
          this.oidcService.logout();
        }
      });
  }

  private registerLocals() {
    registerLocaleData(localeRo, 'ro');
    registerLocaleData(localeEn, 'en');
    registerLocaleData(localeDe, 'de');
  }

  private setContextForIssueReporter(loggedInUser: LoggedInUser) {
    this.appContext['reporter'] = loggedInUser.name;
  }

  protected readonly translationSchema = translationSchema;
}
